import React, { useState, useRef, useEffect } from 'react';
import { toast } from '../../../components/shared/toast';

function PlayerCard({ song, isEditing, editArtPreview, setEditArtPreview, setEditArt }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const currentArtwork = isEditing ? editArtPreview : (song.imageUrl || null);

  useEffect(() => {
    // Stop and reset audio element when track URL changes
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    if (song.trackUrl) {
      audioRef.current = new Audio(song.trackUrl);

      const handleTimeUpdate = () => {
        setCurrentTime(audioRef.current ? audioRef.current.currentTime : 0);
      };
      const handleLoadedMetadata = () => {
        setDuration(audioRef.current ? audioRef.current.duration : 0);
      };
      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
      audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      audioRef.current.addEventListener('ended', handleEnded);

      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
          audioRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
          audioRef.current.removeEventListener('ended', handleEnded);
          audioRef.current = null;
        }
      };
    }
  }, [song.trackUrl]);

  // Selected artwork preview handles current view source

  const handlePlayPause = () => {
    if (!audioRef.current) {
      toast.warning('No track file available to play.');
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(err => {
        console.warn('Playback failed:', err);
        toast.error('Could not play the audio file.');
      });
      setIsPlaying(true);
    }
  };

  const handleArtworkClick = () => {
    fileInputRef.current.click();
  };

  const handleArtworkChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be smaller than 5MB.');
        return;
      }
      if (!file.type.includes('image')) {
        toast.error('Please select an image file.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setEditArtPreview(event.target.result);
        setEditArt(file);
        toast.success('Artwork selected for update. Save details to apply changes.');
      };
      reader.readAsDataURL(file);
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper to generate waveform bars (approx 48 bars matching visual)
  const waveformBars = Array.from({ length: 48 }, (_, i) => {
    const heights = [
      12, 16, 20, 24, 28, 20, 16, 22, 26, 32, 28, 22, 18, 24, 28, 34, 30, 26, 20, 16,
      12, 8, 14, 18, 22, 28, 24, 20, 16, 22, 26, 30, 24, 18, 14, 20, 24, 28, 22, 18,
      14, 10, 8, 12, 16, 12, 8, 6
    ];
    const h = heights[i] || 15;
    const progressPercent = duration ? (currentTime / duration) : 0;
    const activeBarIndex = Math.floor(progressPercent * 48);
    const isActive = i < activeBarIndex;
    return (
      <div
        key={i}
        className={`waveform-bar ${isActive ? 'active' : ''} ${isPlaying && isActive ? 'playing' : ''}`}
        style={{ 
          height: `${h}px`,
          animationDelay: `${i * 0.03}s`
        }}
      />
    );
  });

  return (
    <div className="player-card">
      <div className="player-details-left">
        {/* Status Pill */}
        {(() => {
          const isAct = song.status === 'Active' || song.status === 1 || song.status === true || song.status === '1';
          return (
            <div className="player-status">
              <span className={`status-pill ${isAct ? 'active' : 'inactive'}`}>
                <span className="status-dot"></span>
                {isAct ? 'Active' : 'Inactive'}
              </span>
            </div>
          );
        })()}

        {/* Song Titles */}
        <h2 className="details-song-title">{song.name}.</h2>
        <p className="details-song-subtitle">{song.nameSinhala}</p>

        {/* Audio Player Toolbar */}
        <div className="details-audio-player">
          {/* Play Button */}
          <button
            className="player-play-btn"
            onClick={handlePlayPause}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            )}
          </button>

          {/* Back Button */}
          <button className="player-control-btn" aria-label="Previous Track">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="19 20 9 12 19 4 19 20" />
              <line x1="5" y1="19" x2="5" y2="5" />
            </svg>
          </button>

          {/* Waveform Visualizer */}
          <div className="waveform-container">
            {waveformBars}
          </div>

          {/* Time labels */}
          <div className="player-time-labels">
            <span className="time-current">{formatTime(currentTime)}</span>
            <span className="time-duration">{formatTime(duration)}</span>
          </div>

          {/* Speaker Icon */}
          <button className="player-control-btn" aria-label="Volume">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
          </button>

          {/* Ellipsis Options */}
          <button className="player-control-btn" aria-label="More options">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="1" />
              <circle cx="12" cy="5" r="1" />
              <circle cx="12" cy="19" r="1" />
            </svg>
          </button>
        </div>
      </div>

      {/* Artwork Card Right */}
      <div
        className={`details-artwork-card ${currentArtwork ? 'has-artwork' : ''} ${isEditing ? 'editable' : ''}`}
        onClick={isEditing ? handleArtworkClick : undefined}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleArtworkChange}
        />

        {currentArtwork ? (
          <img src={currentArtwork} alt="Song Artwork" className="artwork-image-display" />
        ) : (
          <>
            <div className="artwork-note-wrapper">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18V5l12-2v13" />
                <circle cx="6" cy="18" r="3" />
                <circle cx="18" cy="16" r="3" />
              </svg>
            </div>
            <span className="artwork-label">Song Artwork</span>
          </>
        )}

        {isEditing && (
          /* Hover overlay edit indicator */
          <div className="artwork-hover-overlay">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Change Artwork</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlayerCard;
